// Oversight-level vocabulary for the per-run run-start selector.
//
// The STORED / WIRE values are the oversight AMOUNT (HIGH asks most, NONE asks
// for nothing) and never change: they ride `inputs.oversight_level`, the
// substrate's per-user preference and every persisted run.
//
// What the AM READS is the inverse, "Comfort Level" (story 28633): how
// comfortable they are letting ora work on its own. More comfort = fewer
// check-ins, so HIGH oversight reads "Low" comfort and NONE reads "Full".
// Only the labels and copy below say "comfort"; identifiers keep "oversight".
//
// `NONE` ("Full") is offered by the selector on both hosts (the add-in drawer
// and ora's triage route). It is the fully-unattended, v1.2-parity level: ora
// asks before no step and commits the endorsement on its own. It is opt-in and
// never the default (the form default is LOW = "High" comfort). It still stops
// for structural work no automation can replace — the carrier handoff, MFA if
// challenged, and the additional-interest picker — exactly as v1.2 does.

export type OversightLevel = "HIGH" | "MEDIUM" | "LOW" | "NONE";

export interface OversightLevelOption {
  value: OversightLevel;
  label: string;       // the COMFORT label, e.g. "High" for value LOW
  description: string; // plain-language copy on what ora does at this level
}

/** The control's label. The concept on screen is comfort, not oversight. */
export const COMFORT_LEVEL_LABEL = "Comfort Level";

// Order: HIGH -> MEDIUM -> LOW -> NONE (most -> least oversight), i.e. least ->
// most comfort.
export const OVERSIGHT_LEVEL_OPTIONS: readonly OversightLevelOption[] = [
  { value: "HIGH",   label: "Low",    description: "ora asks before most steps." },
  { value: "MEDIUM", label: "Medium", description: "ora asks before important steps." },
  { value: "LOW",    label: "High",   description: "ora asks only before the riskiest steps." },
  {
    value: "NONE",
    label: "Full",
    description:
      "ora doesn't ask before steps, including saving the change in Epic. It still stops when it needs information from you.",
  },
];

export const DEFAULT_OVERSIGHT_LEVEL: OversightLevel = "LOW";

/** One line on what comfort means, above the level table in the `?` (ComfortLevelHelp). */
export const COMFORT_LEVEL_SUMMARY =
  "How comfortable you are letting ora work on its own — higher comfort means fewer check-ins.";

/**
 * The comfort label for a stored oversight value ("LOW" -> "High"),
 * case-insensitive. Null for a blank or unknown value, so a caller never shows
 * a raw wire value as if it were a comfort level.
 */
export function comfortLabel(level: string | null | undefined): string | null {
  const key = (level ?? "").trim().toUpperCase();
  if (!key) return null;
  return OVERSIGHT_LEVEL_OPTIONS.find((opt) => opt.value === key)?.label ?? null;
}
